<template>
    <div class="unpacking-sign">
        <div class="us-toolbar">
            <b-button size="sm" variant="secondary" @click="goBack">
                <i class="fa fa-fw fa-arrow-left"></i>
                返回
            </b-button>
            <span class="us-title">开箱检验签字</span>
            <div class="us-right">
                <span class="us-count">已选 {{ selectedIds.length }} 项</span>
                <b-button size="sm" variant="primary"
                          :disabled="signableIds.length === 0 || signing"
                          @click="openSignature">
                    <i v-if="signing" class="fa fa-fw fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-fw fa-signature"></i>
                    手写签字<span v-if="signableIds.length">（{{ signableIds.length }}）</span>
                </b-button>
                <b-button size="sm" variant="success"
                          :disabled="exportableIds.length === 0 || exporting || signing"
                          @click="exportPdf">
                    <i v-if="exporting" class="fa fa-fw fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-fw fa-file-pdf-o"></i>
                    导出签字PDF<span v-if="exportableIds.length">（{{ exportableIds.length }}）</span>
                </b-button>
            </div>
        </div>

        <div class="us-search">
            <div class="us-field">
                <label class="us-label" for="us-project-code">项目号</label>
                <b-form-input id="us-project-code" v-model="form.projectCode" size="sm"
                              placeholder="支持模糊搜索"
                              @focus.native="selectAllOnFocus"
                              @keyup.enter.native="onSearch"></b-form-input>
            </div>
            <div class="us-field">
                <label class="us-label" for="us-package-no">包装编码</label>
                <b-form-input id="us-package-no" v-model="form.packageNo" size="sm"
                              placeholder="支持模糊搜索"
                              @focus.native="selectAllOnFocus"
                              @keyup.enter.native="onSearch"></b-form-input>
            </div>
            <div class="us-field">
                <label class="us-label" for="us-box-no">箱号</label>
                <b-form-input id="us-box-no" v-model="form.boxNo" size="sm"
                              placeholder="支持模糊搜索"
                              @focus.native="selectAllOnFocus"
                              @keyup.enter.native="onSearch"></b-form-input>
            </div>
            <div class="us-field">
                <label class="us-label" for="us-signer-name">经办人</label>
                <b-form-input id="us-signer-name" v-model="form.signerName" size="sm"
                              placeholder="支持模糊搜索"
                              @focus.native="selectAllOnFocus"
                              @keyup.enter.native="onSearch"></b-form-input>
            </div>
            <div class="us-field">
                <label class="us-label" for="us-signed">是否已签名</label>
                <b-form-select id="us-signed" v-model="form.signed" size="sm"
                               :options="signedOptions"></b-form-select>
            </div>
            <div class="us-search-actions">
                <b-button size="sm" variant="primary" :disabled="!canQuery || loading || signing" @click="onSearch">
                    <i v-if="loading" class="fa fa-fw fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-fw fa-search"></i>
                    查询
                </b-button>
                <b-button size="sm" variant="secondary" @click="onReset">
                    <i class="fa fa-fw fa-undo"></i>
                    重置
                </b-button>
                <span v-if="!canQuery" class="us-hint">请输入项目号、包装编码、箱号或经办人</span>
            </div>
        </div>

        <div v-if="exportStatus" class="us-export-bar" :class="'us-export-' + exportStatus">
            <template v-if="exportStatus === 'pending'">
                <i class="fa fa-fw fa-spinner fa-spin"></i>
                <span>正在生成签字PDF，请稍候…</span>
            </template>
            <template v-else-if="exportStatus === 'ready'">
                <i class="fa fa-fw fa-check-circle"></i>
                <span>签字PDF已生成：{{ exportFileName }}</span>
                <b-button size="sm" variant="success" @click="downloadExport">
                    <i class="fa fa-fw fa-download"></i>
                    下载
                </b-button>
            </template>
            <template v-else-if="exportStatus === 'failed'">
                <i class="fa fa-fw fa-exclamation-triangle"></i>
                <span>签字PDF生成失败，请重试</span>
            </template>
            <template v-else>
                <i class="fa fa-fw fa-clock-o"></i>
                <span>生成时间较长，稍后可到通知中心下载：{{ exportFileName }}</span>
            </template>
            <span v-if="exportSkipTip" class="us-export-skip">{{ exportSkipTip }}</span>
            <b-button size="sm" variant="link" class="us-export-close" @click="clearExport">关闭</b-button>
        </div>

        <div class="us-table">
            <mini-table :key="tableKey"
                        ref="miniTable"
                        headerTitle=""
                        disable-header
                        read-only
                        enable-row-check
                        row-click-check
                        effect-origin-row
                        row-key="id"
                        custom-columns="signStatus"
                        :fields="fields"
                        :items="rows"
                        @rowChecked="onChecked">
                <template #custom(signStatus)="{row}">
                    <span v-if="row.signatureUuid" class="us-signed">
                        <b-badge variant="success" class="us-signed-badge" title="点击查看签名"
                                 @click.stop="openSignatureView(row)">已签名</b-badge>
                        <span v-if="row.signerName" class="us-sign-meta">{{ row.signerName }}</span>
                        <span v-if="row.signTime" class="us-sign-meta">{{ row.signTime }}</span>
                    </span>
                    <span v-else class="us-unsigned">未签名</span>
                </template>
            </mini-table>
        </div>

        <div class="us-footer">
            <span>共 {{ rows.length }} 条</span>
            <span v-if="truncated" class="us-truncated">结果可能被截断，请细化搜索条件</span>
        </div>

        <re-b-modal ref="signatureView" header="签字内容" large hide-footer>
            <div class="us-sig-view">
                <div v-if="signatureLoading" class="us-sig-loading">
                    <i class="fa fa-fw fa-spinner fa-spin"></i>
                </div>
                <template v-else-if="currentSignature">
                    <img v-if="signatureImageUrl" :src="signatureImageUrl" class="us-sig-img" alt="signature"/>
                    <div v-else class="us-sig-empty">该签名没有图像附件</div>
                    <div class="us-sig-meta">
                        <p><span>经办人：</span>{{ currentSignature.signerName || currentSignature.signerId }}</p>
                        <p><span>签字时间：</span>{{ currentSignature.signTime }}</p>
                    </div>
                </template>
                <div v-else class="us-sig-empty">暂无签字信息</div>
            </div>
        </re-b-modal>

        <signature-capture-dialog :visible.sync="signatureDialogVisible"
                                  @signed="onSigned"></signature-capture-dialog>
    </div>
</template>
<script>
import MiniTable from "@/components/common/mini-table/MiniTable.vue";
import SignatureCaptureDialog from "@/components/signature/SignatureCaptureDialog.vue";
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal";
import emCommonApiMixin from "@/mixin/em/common/api";
import {getSignature} from "@/utils/signatureApi";

// 本页不分页，必须显式传大 pageSize（ViewObject 默认 pageSize 为 20）
const PAGE_SIZE = 2000;
// bindSignature 的 ids 走 query string，而 sith-mes 未覆盖 server.max-http-header-size，
// 内嵌 Tomcat 默认上限 8KB（请求行 + Authorization JWT 等全部头共用这个额度）。
// 本页不分页且支持表头全选，一次可选到 PAGE_SIZE 条，必须分批提交。
// 100 个 id ≈ 1KB，叠加较大的 JWT 后仍远低于 8KB。
const BIND_BATCH_SIZE = 100;
// 与后端 SithMesInventoryPackageServiceImpl.PDF_BATCH_MAX 保持一致，超限先在前端拦下给出明确提示
const EXPORT_BATCH_MAX = 100;
// PDF 异步生成的轮询节奏与放弃阈值：超时不代表失败，只是不再等，转由通知中心兜底
const EXPORT_POLL_INTERVAL = 2000;
const EXPORT_POLL_TIMEOUT = 180000;
// IsNull / NotNull 是 Where.Op 里的 noValue 分支，值不参与 SQL；
// 传个非空标量只为避开 Where#rewriteValue 对空串做 JSON 校验。
const NO_VALUE_FLAG = '1';

export default {
    name: "sithMesInventoryUnpackingSignView",
    components: {MiniTable, SignatureCaptureDialog, ReBModal},
    mixins: [emCommonApiMixin],
    data() {
        return {
            form: {
                projectCode: '',
                packageNo: '',
                boxNo: '',
                signerName: '',
                signed: '',
            },
            signedOptions: [
                {value: '', text: '全部'},
                {value: 'signed', text: '已签名'},
                {value: 'unsigned', text: '未签名'},
            ],
            rows: [],
            selectedIds: [],
            loading: false,
            // 覆盖「分批提交签名 + 重查」全过程，期间禁用手写签字与查询，避免重复签名覆盖刚写入的 uuid
            signing: false,
            tableKey: 0,
            signatureDialogVisible: false,
            signatureLoading: false,
            currentSignature: null,
            // 导出：受理后端异步生成 + 轮询状态，就地给出下载入口（站内信仍照发，作为兜底）
            exporting: false,
            exportStatus: '',
            exportFileId: null,
            exportFileName: '',
            exportSkipTip: '',
            exportPollTimer: null,
            exportPollDeadline: 0,
            fields: [
                {key: 'projectCode', label: '项目号'},
                {key: 'boxNo', label: '箱号'},
                {key: 'description', label: '装箱内容描述'},
                {key: 'signStatus', label: '签字状态'},
            ],
        };
    },
    computed: {
        canQuery() {
            // 「是否已签名」不算有效条件：单靠它等于空条件全量拉包装箱表，本页不分页扛不住
            return this.trimmed(this.form.projectCode) !== ''
                    || this.trimmed(this.form.packageNo) !== ''
                    || this.trimmed(this.form.boxNo) !== ''
                    || this.trimmed(this.form.signerName) !== '';
        },
        truncated() {
            return this.rows.length >= PAGE_SIZE;
        },
        // 签名图是加密附件，取用方式与 SithMesInventoryPackageList 的签字查看一致
        signatureImageUrl() {
            if (this.currentSignature && this.currentSignature.imageAttachmentId != null) {
                return `${process.env.VUE_APP_EM_ADMIN_URL}/emAttachment/download`
                        + `?attachmentId=${this.currentSignature.imageAttachmentId}`;
            }
            return null;
        },
        rowById() {
            let map = new Map();
            for (let row of this.rows) {
                if (row && row.id != null) {
                    map.set(row.id, row);
                }
            }
            return map;
        },
        // 已签名与未签名行都可勾选，两个按钮各自从选中集里过滤出自己能处理的那部分：
        // 签字只能作用于未签名行，导出 PDF 只能作用于已签名行。
        signableIds() {
            return this.selectedIds.filter(id => {
                let row = this.rowById.get(id);
                return !!row && !row.signatureUuid;
            });
        },
        exportableIds() {
            return this.selectedIds.filter(id => {
                let row = this.rowById.get(id);
                return !!row && !!row.signatureUuid;
            });
        },
        exportDownloadUrl() {
            if (this.exportFileId == null) {
                return null;
            }
            return `${process.env.VUE_APP_REPORT_API_URL}report-service/mmf/report/reportGenerated/download/${this.exportFileId}`;
        },
    },
    mounted() {
        this.$store.commit("base/setShowHeader", false, {root: true});
        this.$store.commit("base/setShowSidebar", false, {root: true});
    },
    beforeDestroy() {
        this.stopExportPolling();
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit("base/setShowHeader", true, {root: true});
        this.$store.commit("base/setShowSidebar", true, {root: true});
        next();
    },
    methods: {
        trimmed(value) {
            return value == null ? '' : String(value).trim();
        },
        goBack() {
            this.$router.back();
        },
        // 重新聚焦即全选，方便现场连续换条件搜索（直接输入即覆盖旧值）。
        // 必须延后一拍：点击进入时 focus 先于 mouseup，浏览器会在 mouseup 把选区收成光标。
        // 延后后若用户是拖拽选词，后续拖拽仍会覆盖这次全选，不影响手动选取。
        selectAllOnFocus(event) {
            let el = event.target;
            setTimeout(() => {
                if (document.activeElement === el && typeof el.select === 'function') {
                    el.select();
                }
            }, 0);
        },
        // 点「已签名」徽标查看签名内容。加 .stop 是为了不让查看动作顺带把该行勾上。
        openSignatureView(row) {
            if (!row || !row.signatureUuid) {
                return;
            }
            this.currentSignature = null;
            this.signatureLoading = true;
            this.$refs.signatureView.showModal();
            getSignature(row.signatureUuid)
                    .then(res => {
                        this.currentSignature = res || null;
                    })
                    .catch(() => {
                        this.$message({message: "签字信息加载失败", type: "error"});
                    })
                    .finally(() => {
                        this.signatureLoading = false;
                    });
        },
        onSearch() {
            if (!this.canQuery || this.loading || this.signing) {
                return;
            }
            this.queryRows();
        },
        onReset() {
            this.form.projectCode = '';
            this.form.packageNo = '';
            this.form.boxNo = '';
            this.form.signerName = '';
            this.form.signed = '';
            this.applyRows([]);
        },
        buildWhere() {
            let where = {};
            // Where#toString 的 ILike 分支会自动包 %，此处传原始字符串
            let projectCode = this.trimmed(this.form.projectCode);
            let packageNo = this.trimmed(this.form.packageNo);
            let boxNo = this.trimmed(this.form.boxNo);
            let signerName = this.trimmed(this.form.signerName);
            if (projectCode !== '') {
                where.projectCodeILike = projectCode;
            }
            if (packageNo !== '') {
                where.packageNoILike = packageNo;
            }
            if (boxNo !== '') {
                where.boxNoILike = boxNo;
            }
            if (signerName !== '') {
                where.signerNameILike = signerName;
            }
            if (this.form.signed === 'signed') {
                where.signatureUuidNotNull = NO_VALUE_FLAG;
            } else if (this.form.signed === 'unsigned') {
                where.signatureUuidIsNull = NO_VALUE_FLAG;
            }
            return where;
        },
        queryRows() {
            this.loading = true;
            return this.$query({
                key: 'inventory_package_sign',
                where: this.buildWhere(),
                page: {pageNumber: 0, pageSize: PAGE_SIZE}
            }).then(res => {
                // api/post 解析出的是完整 axios response，需一层 .data 才到 body
                this.applyRows((res && res.data) || []);
            }).catch(() => {
                // 查询失败必须和「确实一条都没有」区分开：
                // 否则 WHERE_INVALID（jsc 配置缺列）或断网都会被读成「这个项目没有箱子」
                this.applyRows([]);
                this.$message({message: "查询失败，请稍后重试", type: "error"});
            }).finally(() => {
                this.loading = false;
            });
        },
        applyRows(rows) {
            this.rows = Array.isArray(rows) ? rows : [];
            this.selectedIds = [];
            // 重建表格以重置 MiniTable 内部勾选状态（其 checkedIds 不随 items 变化清空）
            this.tableKey++;
        },
        onChecked(ids) {
            // 提交签名期间表格里还是旧数据（刚签的行 signatureUuid 仍为 null），
            // 此时 MiniTable 内部 checkedIds 未重置，任何一次点击都会把整批旧 id 重新抛上来。
            // 直接忽略，杜绝「刚签完又签一次、覆盖掉刚写入的 uuid」。
            if (this.signing) {
                return;
            }
            this.selectedIds = ids.filter(id => this.rowById.has(id));
        },
        openSignature() {
            if (this.signableIds.length === 0 || this.signing) {
                return;
            }
            this.signatureDialogVisible = true;
        },
        onSigned({uuid}) {
            let ids = this.signableIds.slice();
            if (!uuid || ids.length === 0 || this.signing) {
                return;
            }
            this.signing = true;
            this.selectedIds = [];
            this.bindInBatches(ids, uuid)
                    .then(signed => {
                        this.reportBindResult(signed, ids.length);
                    })
                    .catch(() => {
                        // bindInBatches 自身不会 reject，这里只兜住提示环节的意外异常
                        this.$message({message: "签名关联失败，请重试！", type: "error"});
                    })
                    // 成功 / 部分成功 / 全失败都要重查，让表格反映服务端真实状态
                    .then(() => this.refreshAfterSign())
                    .finally(() => {
                        this.signing = false;
                    });
        },
        // 顺序提交各批次，首个失败批次即中止（后续批次不再发出），resolve 已成功的条数。
        // 本方法约定不 reject，失败信息由条数差体现，交给上层统一提示。
        bindInBatches(ids, uuid) {
            let batches = [];
            for (let start = 0; start < ids.length; start += BIND_BATCH_SIZE) {
                batches.push(ids.slice(start, start + BIND_BATCH_SIZE));
            }
            return this.bindBatchAt(batches, uuid, 0, 0);
        },
        bindBatchAt(batches, uuid, index, signed) {
            if (index >= batches.length) {
                return Promise.resolve(signed);
            }
            let batch = batches[index];
            return this.bindOneBatch(batch, uuid).then(
                    () => this.bindBatchAt(batches, uuid, index + 1, signed + batch.length),
                    // 失败分支不再递归，后续批次不会发出
                    () => signed
            );
        },
        bindOneBatch(ids, uuid) {
            let url = `${process.env.VUE_APP_SITHMES_INVENTORY_URL}/sithMesInventoryPackage/bindSignature`
                    + `?ids=${ids.join(',')}&signatureUuid=${encodeURIComponent(uuid)}`;
            return this.$_emApiMixin_post(url, {}).then(response => {
                // store/api.js 已对非 2xx reject，这里只再挡一次 2xx 里的非 200
                if (!response || response.status !== 200) {
                    return Promise.reject(new Error("bindSignature 返回非 200"));
                }
                return true;
            });
        },
        reportBindResult(signed, total) {
            if (signed >= total) {
                this.$message({message: "签名关联成功！", type: "success"});
                return;
            }
            if (signed === 0) {
                this.$message({message: "签名关联失败，请重试！", type: "error"});
                return;
            }
            // 分批提交，失败批次之前的批次已经落库，必须如实报告部分成功
            this.$message({
                message: `已签名 ${signed} 项，剩余 ${total - signed} 项失败，请重试`,
                type: "error"
            });
        },
        refreshAfterSign() {
            // 按当前搜索条件重查，applyRows 会清空勾选并重建表格
            if (this.canQuery) {
                return this.queryRows();
            }
            this.applyRows([]);
            return Promise.resolve();
        },

        // ==================== 导出带签字 PDF ====================
        exportPdf() {
            let ids = this.exportableIds.slice();
            if (ids.length === 0 || this.exporting || this.signing) {
                return;
            }
            if (ids.length > EXPORT_BATCH_MAX) {
                this.$message({
                    message: `一次最多导出 ${EXPORT_BATCH_MAX} 个包装箱，当前已选 ${ids.length} 个，请分批导出`,
                    type: "warning"
                });
                return;
            }
            this.clearExport();
            this.exporting = true;
            // 未签名的箱子不进 PDF，这里先如实告知用户被排除了多少
            let ignored = this.selectedIds.length - ids.length;
            let url = `${process.env.VUE_APP_SITHMES_INVENTORY_URL}/sithMesInventoryPackage/generatePackingListPdfBatch`;
            // ids 放请求体：本接口不可分批（必须一次合成一份 PDF），走 query string 会撞上 8KB 请求头上限
            this.$_emApiMixin_post(url, ids).then(response => {
                let result = (response && response.data) || {};
                if (result.fileId == null) {
                    return Promise.reject(new Error("未返回 fileId"));
                }
                this.exportFileId = result.fileId;
                this.exportFileName = result.fileName || '';
                this.exportStatus = 'pending';
                this.exportSkipTip = this.buildSkipTip(result, ignored);
                this.startExportPolling();
            }).catch(() => {
                this.exporting = false;
                this.$message({message: "导出请求失败，请稍后重试", type: "error"});
            });
        },
        // 未签名的可能来自两处：勾选时就已知未签名（前端过滤掉的），以及后端复核时发现签名记录缺失
        buildSkipTip(result, ignoredByClient) {
            let skipped = (result.skippedCount || 0) + (ignoredByClient > 0 ? ignoredByClient : 0);
            if (skipped <= 0) {
                return '';
            }
            let tip = `已跳过 ${skipped} 个未签字的包装箱`;
            let boxNos = result.skippedBoxNos || [];
            if (boxNos.length > 0) {
                tip += `（${boxNos.slice(0, 5).join('、')}${boxNos.length > 5 ? ' 等' : ''}）`;
            }
            return tip;
        },
        startExportPolling() {
            this.stopExportPolling();
            this.exportPollDeadline = Date.now() + EXPORT_POLL_TIMEOUT;
            this.exportPollTimer = setInterval(() => this.pollExportOnce(), EXPORT_POLL_INTERVAL);
        },
        stopExportPolling() {
            if (this.exportPollTimer) {
                clearInterval(this.exportPollTimer);
                this.exportPollTimer = null;
            }
        },
        pollExportOnce() {
            if (this.exportFileId == null) {
                this.stopExportPolling();
                return;
            }
            let url = `${process.env.VUE_APP_SITHMES_INVENTORY_URL}/sithMesInventoryPackage/packingListPdfStatus`
                    + `?fileId=${this.exportFileId}`;
            // api/get 解析出的就是响应体，这里是裸的状态字符：P 生成中 / R 可下载 / F 失败
            this.$_emApiMixin_get(url).then(status => {
                if (status === 'R') {
                    this.finishExport('ready');
                } else if (status === 'F') {
                    this.finishExport('failed');
                } else if (Date.now() > this.exportPollDeadline) {
                    // 超时只是不再等，报表可能仍在生成，交给通知中心兜底，绝不谎报失败
                    this.finishExport('timeout');
                }
            }).catch(() => {
                // 单次查询失败不下结论，等下一拍；真到超时会走 timeout 分支
                if (Date.now() > this.exportPollDeadline) {
                    this.finishExport('timeout');
                }
            });
        },
        finishExport(status) {
            this.stopExportPolling();
            this.exporting = false;
            this.exportStatus = status;
        },
        downloadExport() {
            if (!this.exportDownloadUrl) {
                return;
            }
            let a = document.createElement('a');
            a.href = this.exportDownloadUrl;
            a.download = ''; // 让浏览器按后端 Content-Disposition 处理
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        clearExport() {
            this.stopExportPolling();
            this.exporting = false;
            this.exportStatus = '';
            this.exportFileId = null;
            this.exportFileName = '';
            this.exportSkipTip = '';
        },
    },
};
</script>
<style lang="scss" scoped>
.unpacking-sign {
    display: flex;
    flex-direction: column;
    height: 100vh;
    box-sizing: border-box;
    padding: 8px;
    background: #f5f6f8;
}

.us-toolbar {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #fff;
    border-radius: 6px;
    flex-wrap: wrap;
    gap: 8px;
}

.us-title {
    font-size: 16px;
    font-weight: bold;
}

.us-right {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.us-count {
    font-size: 13px;
    color: #666;
}

.us-search {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 12px;
    background: #fff;
    border-radius: 6px;
}

.us-field {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.us-label {
    margin: 0;
    font-size: 13px;
    color: #666;
    white-space: nowrap;
}

/* 横屏下输入框定宽，避免挤掉同一行的查询/重置按钮 */
.us-field .form-control {
    width: 180px;
}

.us-search-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.us-hint {
    font-size: 13px;
    color: #e6a23c;
}

.us-table {
    flex: 1;
    min-height: 0;
    margin-top: 8px;
    background: #fff;
    border-radius: 6px;
    padding: 6px;
    overflow: hidden;
    display: flex;
}

.us-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    padding: 4px 12px;
    font-size: 13px;
    color: #666;
}

.us-truncated {
    color: #e6a23c;
    font-weight: 600;
}

.us-signed-badge {
    cursor: pointer;
}

.us-signed-badge:hover {
    filter: brightness(1.1);
    text-decoration: underline;
}

.us-sig-view {
    min-height: 120px;
    text-align: center;
}

.us-sig-loading {
    padding: 40px 0;
    font-size: 28px;
    color: #1E5274;
}

.us-sig-img {
    max-width: 100%;
    max-height: 320px;
    border: 1px solid #e7edf3;
    border-radius: 6px;
    background: #fff;
}

.us-sig-meta {
    margin-top: 10px;
    text-align: left;
    color: #5b6b7a;
    font-size: 13px;
}

.us-sig-meta span {
    font-weight: 600;
}

.us-sig-empty {
    color: #9fb0c0;
    padding: 40px 0;
}

.us-sign-meta {
    margin-left: 6px;
    font-size: 12px;
    color: #666;
}

.us-unsigned {
    color: #909399;
}

/* 竖屏下两个输入框换行堆叠，各占一整行 */
@media (orientation: portrait) {
    .us-field {
        width: 100%;
    }

    .us-field .form-control,
    .us-field .custom-select {
        flex: 1;
        width: auto;
        min-width: 0;
    }
}

/* 让 MiniTable 铺满容器，单元格最小宽度 60px，总宽超出容器时横向滚动 */
.us-table ::v-deep #table {
    height: 100%;
}

.us-table ::v-deep thead th:hover {
    resize: none !important;
}

.us-table ::v-deep thead th,
.us-table ::v-deep tbody td {
    min-width: 60px;
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.us-table ::v-deep tbody td * {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.us-table ::v-deep tbody td .d-flex {
    min-width: 0;
    flex-wrap: wrap;
}

/* 勾选列(第1列)与行号列(第2列)固定 25px 宽 */
.us-table ::v-deep thead th:nth-child(1),
.us-table ::v-deep tbody td:nth-child(1),
.us-table ::v-deep thead th:nth-child(2),
.us-table ::v-deep tbody td:nth-child(2) {
    width: 25px !important;
    min-width: 25px !important;
    max-width: max-content !important;
    padding-left: 2px !important;
    padding-right: 2px !important;
    text-align: center;
}

/* 搜索项变多后，下拉与输入框同宽，横屏下才排得整齐 */
.us-field .custom-select {
    width: 180px;
}

.us-export-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 12px;
    background: #fff;
    border-radius: 6px;
    font-size: 13px;
    border-left: 4px solid #1E5274;
}

.us-export-ready {
    border-left-color: #52c41a;
    color: #2e7d32;
}

.us-export-failed {
    border-left-color: #d9534f;
    color: #a94442;
}

.us-export-timeout {
    border-left-color: #e6a23c;
    color: #a1670a;
}

.us-export-skip {
    color: #e6a23c;
}

.us-export-close {
    margin-left: auto;
    color: #5b6b7a;
}
</style>
